/**
 * API Error Handler with cross-tab single-flight token refresh.
 *
 * Mirrors apps/client. Two layers of dedup: per-tab in-flight promise + cross-tab
 * Web Lock (`navigator.locks`). The cross-tab lock prevents the rotation race —
 * concurrent /refresh calls from sibling tabs would otherwise return 401 to the
 * loser because the server's session-rotation step deletes the prior session.
 */

import { INVALID_CODE_ERROR, SESSION_EXPIRED_ERROR } from '@mondaygpt/constants'
import { StatusCodes } from 'http-status-codes'
import { ApiError, OpenAPI } from '../../../../openapi'
import { store } from '../../../../store'
import { broadcastTokenRefreshed } from '../../../authentication/utils/auth-broadcast'

interface RefreshResult {
  accessToken: string
}

type ApiFunction<T, Args extends unknown[]> = (...args: Args) => Promise<T>
/**
 * RTK `thunkAPI.rejectWithValue` — per-thunk payload types vary, so the value
 * side stays `any` on purpose (bivariant boundary); results are cast to the
 * caller's T at the return sites.
 */
type RejectWithValueFunction = ((value: any) => unknown) | null

const LOG_PREFIX = '🔐 [TokenRefresh]'

const REFRESH_LOCK_NAME = 'taka-auth-refresh'

let inFlightRefresh: Promise<void> | null = null

async function withCrossTabLock<T>(body: () => Promise<T>): Promise<T> {
  const locks = (
    navigator as Navigator & {
      locks?: { request: (name: string, cb: () => Promise<T>) => Promise<T> }
    }
  ).locks
  if (!locks?.request) return body()
  return locks.request(REFRESH_LOCK_NAME, body)
}

export const forceLogout = (): void => {
  console.warn(`${LOG_PREFIX} Performing forced logout due to authentication failure`)
  OpenAPI.TOKEN = undefined
  inFlightRefresh = null

  try {
    import('../../../authentication/slices/authentication-view.slice')
      .then(({ logoutAction }) => {
        store.dispatch(logoutAction())
      })
      .catch(error => {
        console.error(`${LOG_PREFIX} Error importing logoutAction:`, error)
      })
  } catch (error) {
    console.error(`${LOG_PREFIX} Error during logout:`, error)
  }
}

const refreshOnce = (): Promise<void> => {
  if (inFlightRefresh) return inFlightRefresh

  // Snapshot before contending for the lock so we can detect cross-tab updates.
  const tokenBeforeLock = OpenAPI.TOKEN

  inFlightRefresh = (async () => {
    try {
      await withCrossTabLock(async () => {
        const slice = await import('../../../authentication/slices/authentication-view.slice')

        // Don't refresh during a logout — would create a fresh server session
        // that survives the logout via the racing Set-Cookie response.
        if (slice.isLoggingOut) {
          throw new Error('Refresh aborted: logout in progress')
        }

        // Sibling tab beat us to a refresh while we waited for the lock — skip.
        if (OpenAPI.TOKEN && OpenAPI.TOKEN !== tokenBeforeLock) {
          return
        }
        const { refreshTokenAction } = slice

        const businessId = '-1'

        const result: RefreshResult = await store
          .dispatch(refreshTokenAction({ refreshToken: '', businessId }))
          .unwrap()

        if (!result?.accessToken) {
          throw new Error('Refresh response missing access token')
        }

        OpenAPI.TOKEN = result.accessToken
        broadcastTokenRefreshed(result.accessToken)
      })
    } catch (error) {
      console.warn(`${LOG_PREFIX} Refresh failed:`, error)
      forceLogout()
      throw error
    } finally {
      inFlightRefresh = null
    }
  })()

  return inFlightRefresh
}

const handleAuthError = async <T, Args extends unknown[]>(
  error: unknown,
  apiFunction: ApiFunction<T, Args>,
  params: Args,
  rejectWithValue: RejectWithValueFunction
): Promise<T> => {
  try {
    await refreshOnce()
    return await apiFunction(...params)
  } catch (refreshError) {
    console.error(`${LOG_PREFIX} Token refresh failed:`, refreshError)
    return handleGenericError<T>(error, rejectWithValue)
  }
}

const handleOtpError = <T>(error: unknown, rejectWithValue: RejectWithValueFunction): T => {
  if (!rejectWithValue || typeof rejectWithValue !== 'function') throw error

  if (error instanceof ApiError) {
    return rejectWithValue(error.body) as T
  }

  return rejectWithValue({
    statusCode: StatusCodes.UNAUTHORIZED,
    message: INVALID_CODE_ERROR,
  }) as T
}

const handleGenericError = <T>(error: unknown, rejectWithValue: RejectWithValueFunction): T => {
  if (!rejectWithValue || typeof rejectWithValue !== 'function') throw error

  if (error instanceof TypeError) {
    return rejectWithValue({ statusCode: StatusCodes.BAD_REQUEST }) as T
  }

  if (error instanceof ApiError) {
    return rejectWithValue(error.body) as T
  }

  return rejectWithValue(error) as T
}

const handleRejectWithValue = <T>(
  error: unknown,
  rejectWithValue: RejectWithValueFunction,
  message: string
): T => {
  if (!rejectWithValue || typeof rejectWithValue !== 'function') throw error

  return rejectWithValue({
    statusCode: StatusCodes.UNAUTHORIZED,
    message,
  }) as T
}

export const handleApiError = async function <T, Args extends unknown[]>(
  error: unknown,
  apiFunction: ApiFunction<T, Args>,
  params: Args,
  rejectWithValue: RejectWithValueFunction
): Promise<T> {
  if (!apiFunction || typeof apiFunction !== 'function') {
    console.error(`${LOG_PREFIX} Invalid API function provided to error handler`)
    return handleGenericError<T>(error, rejectWithValue)
  }

  if (rejectWithValue && typeof rejectWithValue !== 'function') {
    console.error(`${LOG_PREFIX} Invalid rejectWithValue function provided to error handler`)
    throw error
  }

  const statusCode =
    error instanceof ApiError
      ? error.status
      : (error as { statusCode?: number } | null | undefined)?.statusCode
  const requestUrl = error instanceof ApiError ? error.url : ''

  const isRefreshRequest = requestUrl.includes('/token/refresh')
  const isOtpVerification = requestUrl.includes('/otp/verify')

  if (isOtpVerification) {
    return handleOtpError<T>(error, rejectWithValue)
  }

  if (statusCode === StatusCodes.UNAUTHORIZED && isRefreshRequest) {
    console.warn(`${LOG_PREFIX} Refresh token request failed, forcing logout`)
    forceLogout()
    return handleRejectWithValue<T>(error, rejectWithValue, SESSION_EXPIRED_ERROR)
  }

  if (statusCode === StatusCodes.UNAUTHORIZED) {
    return await handleAuthError(error, apiFunction, params, rejectWithValue)
  }

  return handleGenericError<T>(error, rejectWithValue)
}
